import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { getResponsiveSize } from '../utils/responsive';
import globalStyles from '../global';
import Footer from '../components/HomeComponent/Footer';
import { ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react';
import { mediaAPI } from '../utils/api';

const Blog = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const CARDS_PER_PAGE = 9; // Show 9 cards initially (3 rows x 3 columns)

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const result = await mediaAPI.getMedias();

        if (result.success && result.data && result.data.medias && Array.isArray(result.data.medias)) {
          setNewsData(result.data.medias);
        } else {
          throw new Error('Invalid data format received');
        }
      } catch (error) {
        console.error('Error fetching news:', error);
        setError('Failed to fetch news data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const displayedNews = showAll ? newsData : newsData.slice(0, CARDS_PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', padding: 4 }}>
        <Typography>Loading news...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', padding: 4, color: 'error.main' }}>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.box2}>
        <Box sx={[styles.serviceTitle, { marginY: getResponsiveSize(6) }]}>
          News
        </Box>
        <Grid container spacing={isMobile ? 1 : isTablet ? 2 : 2}>
          {displayedNews.length > 0 ? (
            displayedNews.map((news) => (
              <Grid item xs={12} sm={6} md={4} key={news.id}>
                <Card sx={styles.card}>
                  <Box sx={styles.imageWrapper}>
                    <Box
                      component="img"
                      src={news.full_link}
                      alt={news.title}
                      sx={styles.image}
                    />
                  </Box>
                  <CardContent sx={styles.cardContent}>
                    <Typography sx={styles.dateText}>
                      Date Posted: {new Date(news.published_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                    <Typography sx={styles.newsTitle}>
                      {truncateText(news.title, 60)}
                    </Typography>
                    <Typography sx={styles.newsDescription}>
                      {truncateText(news.description, 100)}
                    </Typography>
                    <Button
                      sx={styles.readMoreButton}
                      href={news.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Read More <ArrowUpRight size={16} />
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography sx={{ textAlign: 'center', padding: 4 }}>
                No news articles available at the moment.
              </Typography>
            </Grid>
          )}
        </Grid>

        {newsData.length > CARDS_PER_PAGE && (
          <Box sx={styles.showMoreContainer}>
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outlined"
              sx={styles.showMoreButton}
              endIcon={showAll ? <ChevronUp /> : <ChevronDown />} 
            >
              {showAll ? 'Show Less' : 'Show More'}
            </Button>
          </Box>
        )}
      </Box>
      <Footer />
    </Box>
  );
};

const styles = {
  box1: {
    backgroundImage: 'url(/news.png)',
    height: getResponsiveSize(320),
    backgroundColor: globalStyles.colors.uclean_green,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box2: {
    width: { xs: '90%', md: '62%' },
    margin: 'auto',
    paddingBottom: 4,
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: 160, // Reduced from 200px
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5', // Light background for image container
    padding: '16px',
  },
  showMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 4,
  },
  showMoreButton: {
    // backgroundColor: globalStyles.colors.uclean_green,
    color: globalStyles.colors.uclean_gold, // Changed text color to golden
    borderColor: globalStyles.colors.uclean_gold, // Changed border color to golden
    padding: '8px 24px',
    borderRadius: '25px',
    textTransform: 'none',
    fontFamily: '"Poppins", serif',
    fontSize: '16px',
    fontWeight: 500,
    width: '100%',
    '&:hover': {
      backgroundColor: globalStyles.colors.uclean_gold,
      color: 'white',
      borderColor: globalStyles.colors.uclean_gold, // Border color on hover
    },
  },
  image: {
    width: 'auto',
    height: '100%',
    maxWidth: '80%', // Ensures image doesn't stretch too wide
    objectFit: 'contain', // Changed from 'cover' to maintain aspect ratio
    display: 'block',
    margin: '0 auto',
  },
  cardContent: {
    padding: '16px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  dateText: {
    fontFamily: '"Poppins", serif',
    fontSize: '12px',
    color: '#666666',
    marginBottom: '4px',
  },
  newsTitle: {
    fontFamily: '"Poppins", serif',
    fontSize: '18px',
    fontWeight: 600,
    color: '#006838',
    lineHeight: 1.3,
    marginBottom: '8px',
  },
  newsDescription: {
    fontFamily: '"Poppins", serif',
    fontSize: '14px',
    color: '#666666',
    lineHeight: 1.5,
    marginBottom: '16px',
    flex: 1,
  },
  readMoreButton: {
    color: '#FFB800',
    fontSize: '14px',
    fontWeight: 500,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    textTransform: 'none',
    fontFamily: '"Poppins", serif',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    marginTop: 'auto',
  },
  serviceTitle:{
    fontFamily: '"Poppins", serif',
    fontSize: '22px',
    fontWeight: 600,
    color:globalStyles.colors.army_green
  }
};

export default Blog;